import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { sellingPointsApi } from 'services/apiDigitalTicket/sellingPoints';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as UsersActions from 'store/ducks/users/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import { CONST_SELLING_POINTS_OBJECT } from 'constants/sellingPoints';

// Types
import { SellingPointsTypes } from './types';

import * as SellingPointsActions from './actions';

function* list({
  payload,
  meta,
}: ReturnType<typeof SellingPointsActions.listSellingPointsRequest>): any {
  try {
    const { body, filtering } = payload;

    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando pontos' }));
    }

    const response = yield call(sellingPointsApi.get, {
      params: { ...body },
    });

    if (response.status === 200) {
      yield put(
        SellingPointsActions.listSellingPointsSuccess({
          sellingPointsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (filtering) meta.promise.resolve(response.data);
      if (!filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - listSellingPoints - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.listSellingPointsFailure());
    if (payload.filtering) {
      meta.promise.reject(error);
    } else yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof SellingPointsActions.getSellingPointRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando ponto' }));

    const response = yield call(sellingPointsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingPointsActions.getSellingPointSuccess({
          sellingPointData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - getSellingPoint - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.getSellingPointFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof SellingPointsActions.filterSellingPointsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sellingPointsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingPointsActions.filterSellingPointsSuccess({
          sellingPointsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - filterSellingPoints - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.filterSellingPointsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof SellingPointsActions.createSellingPointRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando ponto' }));

    const response = yield call(sellingPointsApi.create, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingPointsActions.createSellingPointSuccess({
          sellingPointData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ponto criado com sucesso',
        }),
      );
      yield put(
        UsersActions.createPasswordRequest({ email: response.data.email }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - createSellingPoint - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.createSellingPointFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* update({
  payload,
  meta,
}: ReturnType<typeof SellingPointsActions.updateSellingPointRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando ponto' }));

    const response = yield call(sellingPointsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingPointsActions.updateSellingPointSuccess({
          sellingPointData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ponto alterado com sucesso',
        }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - updateSellingPoint - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.updateSellingPointFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* remove({
  payload,
  meta,
}: ReturnType<typeof SellingPointsActions.deleteSellingPointRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo ponto' }));
    const response = yield call(sellingPointsApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(SellingPointsActions.deleteSellingPointSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ponto excluído com sucesso',
        }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - deleteSellingPoint - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.deleteSellingPointFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* addPayment({
  payload,
  meta,
}: ReturnType<typeof SellingPointsActions.addPaymentSellingPointRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Recebendo repasse' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sellingPointsApi.addPayment, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingPointsActions.addPaymentSellingPointSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Repasse recebido com sucesso',
        }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - addPaymentSellingPoint - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.addPaymentSellingPointFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* changeStatus({
  payload,
  meta,
}: ReturnType<
  typeof SellingPointsActions.changeSellingPointStatusRequest
>): any {
  try {
    let titleSuccess;
    let titleLoading;
    const { newStatus, sellingPointData } = payload;
    if (CONST_SELLING_POINTS_OBJECT.STATUS.ACTIVE.KEY === newStatus) {
      titleLoading = 'Ativando ponto';
      titleSuccess = 'Ponto ativado com sucesso';
    } else {
      titleLoading = 'Bloqueando ponto';
      titleSuccess = 'Ponto bloqueado com sucesso';
    }

    yield put(DialogsActions.setOpenLoading({ title: titleLoading }));

    const body = {
      status: newStatus,
      sellingPointId: sellingPointData.id,
      name: sellingPointData.name,
      type: sellingPointData.type,
      email: sellingPointData.email,
      phone: sellingPointData.phone,
      document: sellingPointData.document,
      allowedAmount: sellingPointData.allowedAmount,
      transportOperatorId: sellingPointData.transportOperatorId,
    };

    const response = yield call(sellingPointsApi.update, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield put(SellingPointsActions.changeSellingPointStatusSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: titleSuccess,
        }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - changeSellingPointStatus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.changeSellingPointStatusFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}
function* changePassword({
  payload,
}: ReturnType<typeof SellingPointsActions.changePasswordRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Resetando senha' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingPointsApi.changePassword, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingPointsActions.changePasswordSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Senha resetada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - Reset Password - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingPointsActions.changePasswordFailure());
    yield put(DialogsActions.setClose());
  }
}
// eslint-disable-next-line import/prefer-default-export
export const sellingPointsSagas = [
  takeLatest(SellingPointsTypes.LIST_SELLING_POINTS_REQUEST, list),
  takeLatest(SellingPointsTypes.GET_SELLING_POINT_REQUEST, get),
  takeLatest(SellingPointsTypes.FILTER_SELLING_POINT_REQUEST, filter),
  takeLatest(SellingPointsTypes.CREATE_SELLING_POINT_REQUEST, create),
  takeLatest(SellingPointsTypes.UPDATE_SELLING_POINT_REQUEST, update),
  takeLatest(SellingPointsTypes.DELETE_SELLING_POINT_REQUEST, remove),
  takeLatest(SellingPointsTypes.ADD_PAYMENT_SELLING_POINT_REQUEST, addPayment),
  takeLatest(
    SellingPointsTypes.CHANGE_SELLING_POINT_STATUS_REQUEST,
    changeStatus,
  ),
  takeLatest(
    SellingPointsTypes.CHANGE_SELLINPOINT_PASSWORD_REQUEST,
    changePassword,
  ),
];
